import { VideoSource } from "../../Enums/enums";

// The batch editor's tabs, derived from the wizard state. One "item" is one
// brand × video × platform card whose source is Edited; items are grouped by
// brand, then by video, in the order the wizard shows them.

export const videoKeyOf = (brandName, wgid) => `${brandName}|${wgid}`;
export const itemKeyOf = (brandName, wgid, platform) => `${brandName}|${wgid}|${platform}`;

/**
 * True when a card reuses another platform's video of the same WGID (Step 3's
 * Duplicate) and that platform is one the batch editor edits.
 */
const duplicatesEdited = (config, videoConfigs) =>
  config?.source === VideoSource.DUPLICATE &&
  !!config.duplicateOf &&
  videoConfigs?.[config.duplicateOf]?.source === VideoSource.EDITED;

/**
 * Every card set to Edited — plus every card that duplicates one of them (a
 * read-only tab, so the batch editor and Step 3 always show the same choice) —
 * grouped `[{brandName, videos: [{key, video, items}]}]`.
 */
export function buildBatchGroups(selectedBrands, brandVideos, brandPlatforms, platformConfigs) {
  return selectedBrands
    .map((brand) => {
      const brandName = brand.name;
      const platforms = brandPlatforms[brandName] || [];
      const videos = (brandVideos[brandName] || [])
        .map((video) => {
          const wgid = video.unique_key;
          const videoConfigs = platformConfigs[brandName]?.[wgid];
          const items = platforms
            .map((platform) => ({ platform, config: videoConfigs?.[platform] }))
            .filter(({ config }) => config?.source === VideoSource.EDITED || duplicatesEdited(config, videoConfigs))
            .map(({ platform, config }) => ({
              key: itemKeyOf(brandName, wgid, platform),
              videoKey: videoKeyOf(brandName, wgid),
              brandName,
              wgid,
              platform,
              video,
              config,
            }));
          return { key: videoKeyOf(brandName, wgid), brandName, video, items };
        })
        .filter((v) => v.items.length > 0);
      return { brandName, videos };
    })
    .filter((g) => g.videos.length > 0);
}

/** The groups flattened to items, in tab order. */
export const flattenItems = (groups) => groups.flatMap((g) => g.videos.flatMap((v) => v.items));

/**
 * Cards that "Set all to Edited" would switch: every selected platform of every
 * video that is not Edited yet. Their other settings are kept, so switching
 * one back restores its local file or duplicate choice.
 */
export function cardsNotEdited(selectedBrands, brandVideos, brandPlatforms, platformConfigs) {
  const out = [];
  selectedBrands.forEach(({ name: brandName }) => {
    (brandVideos[brandName] || []).forEach((video) => {
      (brandPlatforms[brandName] || []).forEach((platform) => {
        const source = platformConfigs[brandName]?.[video.unique_key]?.[platform]?.source;
        if (source !== VideoSource.EDITED) out.push({ brandName, wgid: video.unique_key, platform, source });
      });
    });
  });
  return out;
}

// ── A card's video: the final file, or raw clips ────────────────────────────
// Stored on the card as `rawVideoIds`: a list of clip ids = those clips, merged
// in Admin Portal's order; `[]`, null or absent = the final video.

/** The clip ids a card edits, sorted (order never matters — merge order is fixed). */
export const rawIdsOf = (config) =>
  Array.isArray(config?.rawVideoIds) && config.rawVideoIds.length > 0 ? config.rawVideoIds.map(String).sort() : [];

/** True when two cards edit the same video. */
export const sameVideoChoice = (a, b) => rawIdsOf(a).join(",") === rawIdsOf(b).join(",");

/** Raw clips exist only for WooGlobe WGIDs, not compilations or native videos. */
export const canUseRawClips = (video) => !video?.is_compilation && !video?.is_native_video;

// ── Leaving the batch editor ────────────────────────────────────────────────

/**
 * The source a card goes back to when it is removed from the batch editor:
 * what it had before "Set all to Edited", else the wizard's own default
 * (a compilation's file is LOCAL; anything else ORIGINAL).
 */
export const sourceAfterBatch = (item) => {
  const before = item.config?.sourceBeforeBatch;
  if (before && before !== VideoSource.EDITED) return before;
  return item.video?.is_compilation ? VideoSource.LOCAL : VideoSource.ORIGINAL;
};

/**
 * Where to go when `removed` tabs leave and one of them is open: the next
 * platform of the same video, else the next video (the same platform when it
 * has one), else the one before. Null when nothing is left.
 */
export function tabAfterRemoval(items, removedKeys, activeItem) {
  const left = items.filter((i) => !removedKeys.includes(i.key));
  if (left.length === 0) return null;
  const at = items.findIndex((i) => i.key === activeItem.key);
  const sameVideo = left.filter((i) => i.videoKey === activeItem.videoKey);
  if (sameVideo.length > 0) {
    return sameVideo.find((i) => items.indexOf(i) > at) || sameVideo[sameVideo.length - 1];
  }
  const videoKeys = [...new Set(items.map((i) => i.videoKey))];
  const here = videoKeys.indexOf(activeItem.videoKey);
  const nextVideo = videoKeys.slice(here + 1).find((k) => left.some((i) => i.videoKey === k));
  const prevVideo = videoKeys.slice(0, here).reverse().find((k) => left.some((i) => i.videoKey === k));
  const target = nextVideo || prevVideo;
  const candidates = left.filter((i) => i.videoKey === target);
  return candidates.find((i) => i.platform === activeItem.platform) || candidates[0];
}

// ── Apply to all: reviewed or not ───────────────────────────────────────────

/**
 * True when a card's edit is ready to publish: it has one, and it is not an
 * Apply-to-all copy nobody has opened and saved yet (`batchApplied`).
 */
export const hasReviewedEdit = (config) => Boolean(config?.renderPayload) && !config?.batchApplied;
